import classNames from 'classnames';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Checkbox } from '../Checkbox/Checkbox';

interface PopoverProps {
	buttonContent: React.ReactNode;
	popoverContent: (onClose: () => void) => React.ReactNode;
	buttonClassName?: string;
	popoverClassName?: string;
	/** Applied to the wrapper alongside `fh-popover__container`, for triggers that are not buttons. */
	containerClassName?: string;
	onAfterClose?(): void;
	align?: 'left' | 'right';
	hasPadding?: boolean;
	disableOutsideClose?: boolean;
	hideCount?: boolean;
	disabled?: boolean;
	buttonTestId?: string;
	/**
	 * Render the dropdown in a portal anchored with `position: fixed` to the trigger button,
	 * so it escapes any `overflow` ancestor (e.g. a horizontally-scrolling table) instead of
	 * being clipped. Opt-in — leaves the default absolute-positioned behaviour untouched.
	 */
	floating?: boolean;
	/**
	 * Also open on hover, for triggers whose content is an explanation rather than a menu.
	 *
	 * <p>Only where the pointer can actually hover: `(hover: hover)` is false on a phone, where a
	 * `mouseenter` is synthesised by the tap that is about to toggle it — so without the guard a
	 * tap would open it and immediately close it again. Opt-in, like `floating`; a popover that
	 * holds a form or a list of options should not open because the pointer crossed it.</p>
	 */
	openOnHover?: boolean;
	/**
	 * Keep the click to this popover.
	 *
	 * <p>For a trigger that sits inside something else clickable. The ticket card is one press to
	 * choose the sailing and another to put it back, so opening a note or a capacity's explanation
	 * inside it also threw the sailing out of the booking. Opt-in: a popover in a toolbar has
	 * nothing above it to protect, and silently swallowing events there would be its own surprise.</p>
	 */
	stopPropagation?: boolean;
}

export default function Popover({
	buttonContent,
	popoverContent,
	buttonClassName,
	popoverClassName,
	containerClassName,
	onAfterClose,
	align = 'left',
	hasPadding,
	disableOutsideClose,
	hideCount = false,
	disabled = false,
	buttonTestId,
	floating = false,
	openOnHover = false,
	stopPropagation = false,
}: PopoverProps) {
	const [isOpen, setIsOpen] = useState(false);
	const [floatingCoords, setFloatingCoords] = useState<{ top: number; left?: number; right?: number } | null>(null);
	const buttonRef = useRef<HTMLButtonElement | null>(null);
	const popoverRef = useRef<HTMLDivElement | null>(null);

	const onClose = () => {
		onAfterClose?.();
		setIsOpen(false);
	};

	const togglePopover = () => {
		if (disabled) {
			return;
		}
		if (isOpen) {
			onClose();
		} else {
			setIsOpen(true);
		}
	};

	const handleClickOutside = (event: MouseEvent) => {
		const isPopover = popoverRef.current && !popoverRef.current.contains(event.target as Node);
		const isButton = buttonRef.current && !buttonRef.current.contains(event.target as Node);
		if (!disableOutsideClose) {
			if (isPopover && isButton) {
				onClose();
			}
		}
	};

	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
		// Dependecies of props are important, otherwise weird closure problems will arise
	}, [buttonContent, popoverContent, buttonClassName, popoverClassName, onAfterClose]);

	// Escape closes it, on the same terms as an outside click: a caller that has pinned the popover
	// open wants it to stay open however the user asks.
	useEffect(() => {
		if (!isOpen || disableOutsideClose) {
			return undefined;
		}
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				onClose();
			}
		};
		document.addEventListener('keydown', onKeyDown);
		return () => document.removeEventListener('keydown', onKeyDown);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen, disableOutsideClose, onAfterClose]);

	// When floating, anchor the portalled dropdown to the trigger using viewport coordinates,
	// and keep it pinned while the user scrolls/resizes. useLayoutEffect avoids a first-paint flash.
	useLayoutEffect(() => {
		if (!floating || !isOpen) {
			return undefined;
		}
		const updateCoords = () => {
			const rect = buttonRef.current?.getBoundingClientRect();
			if (!rect) {
				return;
			}
			// Default to dropping below the trigger, but flip above it when there isn't enough
			// room below (and there's more room above) so the menu doesn't run off the screen.
			const popoverHeight = popoverRef.current?.offsetHeight ?? 0;
			const spaceBelow = window.innerHeight - rect.bottom;
			const openUpwards = spaceBelow < popoverHeight + 8 && rect.top > spaceBelow;
			const top = openUpwards ? Math.max(8, rect.top - 8 - popoverHeight) : rect.bottom + 8;
			/*
				Kept on the screen sideways as well as vertically.

				The panel was pinned to the trigger's own edge, which is right until the trigger is
				near one of the screen's: a 300px panel opened from an icon 60px from the right of a
				phone hangs 240px into nowhere, and the only way back is to scroll a page that does
				not scroll sideways. It now slides along to stay inside the viewport, with the same
				8px it keeps from the top and bottom.
			*/
			const gutter = 8;
			const popoverWidth = popoverRef.current?.offsetWidth ?? 0;
			const furthest = Math.max(gutter, window.innerWidth - popoverWidth - gutter);

			if (align === 'right') {
				setFloatingCoords({
					top,
					right: Math.min(Math.max(gutter, window.innerWidth - rect.right), furthest),
				});
			} else {
				setFloatingCoords({ top, left: Math.min(Math.max(gutter, rect.left), furthest) });
			}
		};
		updateCoords();
		// Capture phase so we also catch scrolling of inner containers (e.g. the table wrapper).
		window.addEventListener('scroll', updateCoords, true);
		window.addEventListener('resize', updateCoords);
		return () => {
			window.removeEventListener('scroll', updateCoords, true);
			window.removeEventListener('resize', updateCoords);
		};
	}, [floating, isOpen, align]);

	// Resolved once rather than read on every event: a device does not grow a pointer mid-session,
	// and `matchMedia` is unavailable when this renders server-side or under a bare JSDOM.
	const canHover =
		openOnHover && typeof window !== 'undefined' && window.matchMedia?.('(hover: hover)').matches === true;

	return (
		<div
			className={classNames('fh-popover__container', containerClassName, { disabled: disabled })}
			onMouseEnter={canHover ? () => !disabled && setIsOpen(true) : undefined}
			onMouseLeave={canHover ? () => isOpen && onClose() : undefined}
		>
			<button
				ref={buttonRef}
				type="button"
				onClick={event => {
					if (stopPropagation) {
						event.stopPropagation();
					}
					togglePopover();
				}}
				className={classNames('fh-popover__trigger', buttonClassName)}
				data-test-id={buttonTestId}
			>
				{buttonContent}
			</button>

			{isOpen &&
				(() => {
					const content = (
						<div
							ref={popoverRef}
							className={classNames(
								'fh-popover__content',
								{
									'fh-popover__content--padding': hasPadding,
									'fh-popover__content-anchor--right': align === 'right',
									'fh-popover__content-anchor--left': align === 'left',
									'fh-popover__content--floating': floating,
								},
								popoverClassName,
							)}
							style={
								floating && floatingCoords
									? {
											position: 'fixed',
											top: floatingCoords.top,
											left: floatingCoords.left,
											right: floatingCoords.right,
											marginTop: 0,
										}
									: undefined
							}
						>
							{popoverContent(() => onClose())}
						</div>
					);
					// Portal into the nearest style-scope root (`.frontend`/`.admin`) rather than
					// `document.body`. All frontend styles are nested under `.frontend` in main.scss,
					// so a body-level portal would render the menu completely unstyled. `position: fixed`
					// still lets it escape the table's overflow clipping.
					const portalTarget = buttonRef.current?.closest('.frontend, .admin') ?? document.body;
					return floating ? ReactDOM.createPortal(content, portalTarget) : content;
				})()}
		</div>
	);
}

interface PopoverCheckboxItemProps {
	value: boolean;
	label: React.ReactNode;
	onChecked(newValue: boolean): void;
}
export function PopoverCheckboxItem({ value, label, onChecked }: PopoverCheckboxItemProps) {
	return (
		<Checkbox
			className="fh-popover__checkbox fh-popover__list-item"
			model={{ value }}
			modelProperty="value"
			label={label}
			onChecked={(_, newValue) => {
				onChecked(newValue);
			}}
		/>
	);
}
